import React, { useState } from 'react';
import './SignInModal.css';
import { API_BASE_URL } from '../config';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

const SignInModal = ({ onClose, onSuccessfulLogin, initialMode = "login" }) => {

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [mode, setMode] = useState(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsLoading(true);
  setError('');

  try {
    const response = await fetch(`${API_BASE_URL}/${mode}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",   // needed for cookies/session
      body: JSON.stringify({
        email: formData.email,
        password: formData.password
      })
    });

    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error("Invalid response format");
    }

    if (response.ok && data.status === "success") {
      localStorage.setItem("user", JSON.stringify({
          email: formData.email,
          isAuthenticated: true,
          is_admin: data.is_admin   // store admin flag
        }));
      if (formData.rememberMe) {
        localStorage.setItem("rememberedEmail", formData.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
      onSuccessfulLogin?.();
      onClose?.();
    } else {
      setError(data.message || "Invalid credentials");
    }
  } catch (err) {
    setError(err.message || "Server error. Please try again.");
  } finally {
    setIsLoading(false);
  }
};

const handleGoogleSuccess = (credentialResponse) => {
  try {
    const decoded = jwtDecode(credentialResponse.credential);
    console.log("Google Login Success:", decoded);
    
    const userData = {
      email: decoded.email,
      name: decoded.name,
      picture: decoded.picture,
      isAuthenticated: true,
      is_admin: false,
      isGoogleAuth: true
    };

    localStorage.setItem("user", JSON.stringify(userData));
    
    onSuccessfulLogin?.(userData);
    onClose?.();
  } catch (err) {
    console.error("Failed to decode Google JWT", err);
    setError("Google Login failed. Please try again.");
  }
};

const handleGoogleError = () => {
  console.error("Google Login Failed");
  setError("Google Login failed. Please try again.");
};

  return (
    <div className="signin-modal-container">
      {/* ═══ LEFT PANEL — Visual Storytelling ═══ */}
      <div className="signin-modal-left">
        {/* Logo */}
        <div className="sim-logo">
          <div className="sim-logo-icon">S4</div>
          <span className="sim-logo-name">stocks4u</span>
        </div>

        {/* Heading */}
        <div className="sim-heading">
          <h1>
            Smarter insights.<br />
            <span className="sim-highlight">Better</span> decisions.
          </h1>
        </div>

        <p className="sim-subtext">
          Real-time market data, powerful analytics, and portfolio insights — all in one place.
        </p>

        {/* Floating Analytics Card */}
        <div className="sim-analytics-card">
          <div className="sim-card-label">Portfolio Value</div>
          <div className="sim-card-value">$24,735.64</div>
          <div className="sim-card-change">
            <span>▲</span> +6.32% Today
          </div>

          {/* SVG Line Chart */}
          <div className="sim-chart-wrap">
            <svg viewBox="0 0 280 65" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="simChartFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7B61FF" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#7B61FF" stopOpacity="0" />
                </linearGradient>
                <filter id="simGlow">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <path
                d="M0,55 Q25,52 50,44 T100,35 T150,38 T200,22 T250,16 L280,10 L280,65 L0,65 Z"
                fill="url(#simChartFill)"
              />
              <path
                d="M0,55 Q25,52 50,44 T100,35 T150,38 T200,22 T250,16 L280,10"
                stroke="#7B61FF"
                strokeWidth="2"
                fill="none"
                filter="url(#simGlow)"
                strokeLinecap="round"
              />
              <circle cx="280" cy="10" r="3.5" fill="#7B61FF">
                <animate attributeName="r" values="2.5;4.5;2.5" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="280" cy="10" r="7" fill="#7B61FF" opacity="0.2">
                <animate attributeName="r" values="5;9;5" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.25;0.08;0.25" dur="2s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>

          {/* Stats */}
          <div className="sim-card-stats">
            <div className="sim-stat">
              <div className="sim-stat-lbl">Holdings</div>
              <div className="sim-stat-val">28</div>
            </div>
            <div className="sim-stat">
              <div className="sim-stat-lbl">Watchlist</div>
              <div className="sim-stat-val">16</div>
            </div>
            <div className="sim-stat">
              <div className="sim-stat-lbl">Alerts</div>
              <div className="sim-stat-val">7</div>
            </div>
          </div>
        </div>

        {/* Security Badge */}
        <div className="sim-security">
          <div className="sim-security-icon">🛡️</div>
          <div className="sim-security-text">
            <h4>Bank-level security</h4>
            <p>256-bit encryption • Secure • Private</p>
          </div>
        </div>
      </div>

      {/* ═══ RIGHT PANEL — Sign In Form ═══ */}
      <div className="signin-modal-right">
        <GoogleOAuthProvider clientId="YOUR_GOOGLE_CLIENT_ID">
          <div className="modal-form-container">
            <h2 className="sim-form-title">
              {mode === "login" ? "Welcome back" : "Create account"}
            </h2>
            <p className="sim-form-subtitle">
              {mode === "login"
                ? "Sign in to continue to your account"
                : "Get started with your free account"}
            </p>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="sim-input-group">
                <label className="sim-input-label">Email address</label>
                <div className="sim-input-wrapper">
                  <span className="sim-input-icon">✉</span>
                  <input
                    type="email"
                    name="email"
                    className="sim-input"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="sim-input-group">
                <label className="sim-input-label">Password</label>
                <div className="sim-input-wrapper">
                  <span className="sim-input-icon">🔒</span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="sim-input"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    style={{ paddingRight: '44px' }}
                  />
                  <button
                    type="button"
                    className="sim-pwd-toggle"
                    onClick={() => setShowPassword(prev => !prev)}
                    tabIndex={-1}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              {/* Options row */}
              {mode === "login" && (
                <div className="sim-options-row">
                  <label className="sim-checkbox-wrap">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                    />
                    <span className="sim-checkmark">
                      <svg className="sim-checkmark-tick" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    Remember me
                  </label>
                  <a href="#" className="sim-forgot-link" onClick={(e) => e.preventDefault()}>
                    Forgot password?
                  </a>
                </div>
              )}

              {/* Error */}
              {error && <div className="sim-error">{error}</div>}

              {/* Submit */}
              <button type="submit" className="sim-submit-btn" disabled={isLoading}>
                {isLoading
                  ? (mode === "login" ? "Signing In..." : "Creating Account...")
                  : (mode === "login" ? "Sign In" : "Sign Up")}
              </button>
            </form>

            {/* Switch mode link */}
            <div className="sim-switch-text">
              {mode === "login" ? (
                <p>
                  Don't have an account?{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); setMode("signup"); }}>
                    Sign up
                  </a>
                </p>
              ) : (
                <p>
                  Already have an account?{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); setMode("login"); }}>
                    Sign in
                  </a>
                </p>
              )}
            </div>

            {/* Divider */}
            <div className="sim-divider">
              <div className="sim-divider-line" />
              <span className="sim-divider-text">OR</span>
              <div className="sim-divider-line" />
            </div>

            {/* Google Login */}
            <button type="button" className="sim-google-btn" onClick={() => {
              const googleBtn = document.querySelector('[data-type="standard"]');
              if (googleBtn) googleBtn.click();
            }}>
              <svg className="sim-google-icon" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </button>

            {/* Hidden actual Google Login for OAuth */}
            <div style={{ display: 'none' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_black"
                shape="rectangular"
                size="large"
                text="continue_with"
                width="100%"
              />
            </div>

            {/* Privacy footer */}
            <div className="sim-privacy">
              🔒 We never share your data with third parties.
            </div>
          </div>
        </GoogleOAuthProvider>
      </div>
    </div>
  );
};


export default SignInModal;